import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import useDebounce from '@/client/hooks/useDebounce';
import { useClientStore } from '@/client/stores/client';
import { placeLabel, type PlaceHit } from './place-label';

const MIN_QUERY_LENGTH = 3;
const REMOTE = 'Remote';

function usePlaceSearch(query: string) {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: ['hr', 'place-search', query],
    enabled: query.length >= MIN_QUERY_LENGTH,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<PlaceHit[]> => {
      try {
        const raw = await request<{ data?: unknown } | unknown[]>(
          `/api/v2/location/name?name=${encodeURIComponent(query)}&include_hierarchy=true&min_hierarchy_level=1.0`,
        );
        const data = Array.isArray(raw) ? raw : (raw as { data?: unknown })?.data;
        const list = Array.isArray(data) ? data : data ? [data] : [];
        return (list as PlaceHit[]).filter((p) => p?.lid && p.active !== 0).slice(0, 8);
      } catch {
        return []; // the API answers 404 when nothing matches
      }
    },
  });
}

/**
 * Location for an employee: search places the way listings do and pick one,
 * or keep typing free text (e.g. a city outside the gazetteer). Saves text only.
 */
export function LocationSearchInput({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const query = useDebounce(value.trim(), 250);
  const { data: hits = [], isFetching } = usePlaceSearch(query);
  const searching = query.length >= MIN_QUERY_LENGTH;

  const pick = (label: string) => {
    onChange(label);
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={className}
        placeholder="Search a place, or Remote"
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white shadow">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick(REMOTE)}
            className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
          >
            {REMOTE}
          </button>
          {searching &&
            (isFetching && !hits.length ? (
              <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
            ) : hits.length ? (
              hits.map((hit) => (
                <button
                  key={hit.lid}
                  type="button"
                  // Keep focus so the click lands before the list closes.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(placeLabel(hit))}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                >
                  {placeLabel(hit)}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">No matching place. What you typed is kept.</div>
            ))}
          {!searching && (
            <div className="px-3 py-2 text-xs text-gray-500">Type 3 or more letters to search places.</div>
          )}
        </div>
      )}
    </div>
  );
}
